// =============================================================================
// PROJECTOR PAGE — Minimal slideshow + video display (no chat)
// Socket connection, slideshow sync from host owner, fullscreen toggle
// =============================================================================

(function() {
  'use strict';

  // Add template class for CSS scoping
  document.body.classList.add('projector-template');

  // Slideshow visibility (always on for projector)
  window.isSlideshowVisible = true;
  var currentImages = [];
  var currentSlideIndex = 0;
  var currentProductHandle = null;

  // ---- Socket.IO connection ----
  var config = window.MHV_LIVE_CONFIG || {};
  var customer = config.customer || {};

  // ⚠️ socket.io.js comes FROM the Heroku app, so a dyno restart while this page is
  // loading leaves `io` undefined and the next line throws — on a screen bolted to a
  // wall, in a room where nobody is watching a console. It would simply sit there
  // showing the last frame it had, or nothing, for the whole sale.
  //
  // So this one RECOVERS BY ITSELF rather than asking a human: say what happened, then
  // reload every 15s until the server answers. Deliberately different from the host
  // page, which has an operator standing in front of it who can press a button.
  if (typeof io === 'undefined') {
    var warn = document.createElement('div');
    warn.setAttribute('style', 'position:fixed;inset:0;z-index:2147483647;background:#000;color:#fff;' +
      'display:flex;flex-direction:column;align-items:center;justify-content:center;gap:20px;' +
      'font:600 30px/1.4 system-ui,sans-serif;text-align:center;padding:40px;');
    warn.innerHTML = '<div>Reconnecting to the auction…</div>' +
      '<div style="font-weight:400;font-size:20px;opacity:.75">The display will come back on its own.</div>';
    (document.body || document.documentElement).appendChild(warn);
    setTimeout(function () { location.reload(); }, 15000);
    return;
  }

  var socket = io(MHV.SERVER_URL, {
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 5000,
    transports: ['websocket', 'polling'],
    // WHICH WORLD THIS SCREEN IS IN — decided at HANDSHAKE time.
    // The customer id was already being sent, but in the `new-user` payload
    // below, which arrives AFTER the server has decided whether this connection
    // belongs to a rehearsal. So the projector could never be recognised as
    // staff and never joined a simulation: it sat on the real auction showing
    // PHOTO-GALLERY while the sim ran LOT001.
    // Function form, so a reconnect re-reads it rather than replaying the value
    // captured when the page first loaded.
    auth: function (cb) { cb({ customerId: (config.customer && config.customer.id) || null }); }
  });
  window.socket = socket;

  // A simulation starting or ending changes which world this screen belongs to,
  // and that is fixed at handshake — so the socket has to shake hands again.
  // This screen runs unattended for hours; without this, starting a rehearsal
  // would mean walking over and reloading the projector.
  ['simulation-started', 'simulation-ended'].forEach(function (evt) {
    socket.on(evt, function () {
      try { socket.disconnect(); socket.connect(); } catch (e) { /* reconnect logic will retry */ }
    });
  });

  // ── STAY PROJECTING ───────────────────────────────────────────────────────
  // This screen runs unattended in front of a room all day, so every failure has to
  // recover ITSELF — nobody is watching the console, and "go and refresh the projector"
  // is a person walking across a hall mid-sale. Two independent failures, two watchdogs
  // (both armed at the bottom of this file):
  //
  //   1. THE SOCKET DIES QUIETLY. A Wi-Fi blip, a dyno restart, or a tab the OS
  //      suspended and resumed can leave a socket that still believes it is connected.
  //      The server sends an app-level `heartbeat` every 20s, so silence longer than
  //      that is real evidence, not just a quiet auction.
  //
  //   2. THE HOST STOPS DRIVING. Slides advance ONLY because the host tab that owns the
  //      slideshow broadcasts each change. Chrome throttles timers in a background tab,
  //      so with several host tabs open the owner can go quiet while the visible one
  //      looks perfectly fine — and the projector sits on one photo.
  var lastServerMsg = Date.now();     // any event from the server, heartbeat included
  var lastConnectedAt = Date.now();
  var lastHostSlideAt = 0;            // when the HOST last drove a slide
  var hostPlaying = true;             // the host's own isPlaying — a deliberate pause
                                      // must not be overridden by the fallback
  if (typeof socket.onAny === 'function') {
    socket.onAny(function() { lastServerMsg = Date.now(); });
  }

  socket.on('connect', function() {
    console.log('Projector connected to server');
    lastServerMsg = Date.now();
    lastConnectedAt = Date.now();
    socket.emit('new-user', {
      screenName: customer.screenName || 'Projector',
      firstName: customer.firstName || '',
      lastName: customer.lastName || '',
      isLoggedIn: !!customer.id,
      customerId: customer.id || null,
      bidderNumber: customer.bidderNumber || '',
      badge: customer.badge || '',
      profileImage: customer.profileImage || '',
      deviceInfo: typeof MHV !== 'undefined' && MHV.getDeviceInfo ? MHV.getDeviceInfo() : {}
    });
    // ⚠️ ASK for the current bid state (tweak 7). The projector only ever LISTENED
    // for live-bid-state pushes, so a screen opened (or reconnected, or refreshed)
    // mid-sale showed an empty bid panel until the NEXT bid landed — which on a
    // slow lot is a long time to be projecting nothing to a room. The server
    // answers this to the requesting socket alone.
    socket.emit('request-live-bid-state');
  });

  socket.on('disconnect', function() {
    console.log('Projector disconnected');
  });

  socket.on('heartbeat', function() {
    socket.emit('heartbeat-response');
  });

  // ---- Title display ----
  var titleEl = document.getElementById('projector-lot-title');
  var subEl = document.getElementById('projector-lot-sub');
  var lotBadgeEl = document.getElementById('projector-lot-badge');
  var absenteeBadgeEl = document.getElementById('projector-absentee-badge'); // removed from DOM (null); legacy guards short-circuit
  var bidPanelEl = document.getElementById('projector-bid-panel');
  var pbpCurAmtEl = document.getElementById('pbp-current-amount');
  var pbpHolderEl = document.getElementById('pbp-holder');
  var pbpAskAmtEl = document.getElementById('pbp-ask-amount');
  // The current row is now two GRID CELLS (label+badge, amount), so hiding it means
  // hiding both — a single querySelector left the amount stranded in the grid.
  var pbpCurCells = bidPanelEl ? bidPanelEl.querySelectorAll('.pbp-current-cell') : [];
  var pbpAskCells = bidPanelEl ? bidPanelEl.querySelectorAll('.pbp-ask-cell') : [];

  function pbpMoney(n) { n = Number(n || 0); return '$' + (n % 1 === 0 ? String(n) : n.toFixed(2)); }

  // The lot the panel is currently showing, and whether it ever had numbers worth
  // showing. Both exist so a transient push cannot blank a panel a room full of
  // people is reading.
  var pbpProductId = null;
  // The host's WALL toggle (bidding widget → set-projector-bid-panel). While off,
  // every push is still tracked — state, lot, sold — but the panel paints nothing,
  // so switching it back on is a repaint, not a wait for the next bid.
  var wallHidden = false;
  var pbpHasContent = false;
  var liveBidCounts = {};   // productId -> bids, from live-bid-state (DB truth)
  var currentProductForBadge = null;

  // Render the current-bid + holder (ABSENTEE/FLOOR) + ask panel from live-bid-state.
  //
  // ⚠️ ONCE UP, IT STAYS UP for the lot on the block. The panel used to be hidden by
  // any state that happened to carry no numbers -- `(hasCur || ask > 0 || d.isOpen)`
  // is false for a perfectly ordinary push between bids -- so the current/ask display
  // appeared and then vanished a beat later, mid-sale, in front of the room. It now
  // comes down only when the lot actually CHANGES or is explicitly cleared
  // (productId null), which are the two cases where what it says would be wrong.
  function renderBidPanel(d) {
    // Remember what the ENGINE says about this lot. The absentee badge below used to
    // read a Shopify metafield, which only refreshes on a sync — so a lot whose
    // absentee bids arrived after that sync showed no badge to the room. Same fault
    // that hid late bids from the host page on 20260820.
    if (d && d.productId) {
      liveBidCounts[String(d.productId).replace(/[^0-9]/g, '')] = Number(d.numberOfBids) || 0;
      refreshAbsenteeBadgeFor(d.productId);
    }
    if (!bidPanelEl) return;
    if (!d || !d.productId) {
      bidPanelEl.style.display = 'none';
      pbpProductId = null; pbpHasContent = false;
      return;
    }
    // A different lot starts over: the previous lot's numbers must never linger.
    if (String(d.productId) !== String(pbpProductId)) {
      pbpProductId = String(d.productId);
      pbpHasContent = false;
    }
    var cur = Number(d.currentBid || 0);
    var ask = Number(d.ask || 0);

    // ---- NOTHING GOES ON THE WALL BEFORE THE AUCTIONEER OPENS THE LOT -------
    // A lot with standing ABSENTEE bids arrives with a current bid already on it,
    // so the room was reading a live-looking figure for a lot nobody had opened —
    // and an absentee's standing bid shown early is a number the room can start
    // reacting to before it has been called.
    //
    // `announced` is the server's record of Open Bidding actually being pressed. A
    // FLOOR holder also counts: the only way the floor holds a lot is that someone
    // bid in the room, which means bidding is plainly underway whatever the flag
    // says. An absentee holder does NOT count — that is exactly the pre-open case.
    var announced = (d.announced === true) || d.holder === 'floor';

    var hasCur = announced && cur > 0;
    // No "current" before any bid has landed.
    for (var ci = 0; ci < pbpCurCells.length; ci++) {
      pbpCurCells[ci].style.display = hasCur ? '' : 'none';
    }
    if (hasCur && pbpCurAmtEl) pbpCurAmtEl.textContent = pbpMoney(cur);
    if (pbpHolderEl) {
      if (hasCur && d.holder) {
        pbpHolderEl.textContent = d.holder === 'floor' ? 'FLOOR' : 'ABSENTEE';
        pbpHolderEl.className = 'pbp-holder pbp-holder--' + d.holder;
        pbpHolderEl.style.display = '';
      } else { pbpHolderEl.style.display = 'none'; pbpHolderEl.textContent = ''; }
    }
    // The ask waits on the same signal as the current — see above. It used to
    // treat "carries a bid" as announced, which let an absentee lot put its ask up
    // before it was opened.
    var showAsk = announced && ask > 0;
    for (var ai = 0; ai < pbpAskCells.length; ai++) {
      pbpAskCells[ai].style.display = showAsk ? '' : 'none';
    }
    if (pbpAskAmtEl) pbpAskAmtEl.textContent = showAsk ? pbpMoney(ask) : '—';
    if (hasCur || showAsk) pbpHasContent = true;
    bidPanelEl.style.display = (pbpHasContent && !wallHidden) ? '' : 'none';
  }

  // setTitle(text) alone CLEARS the subline — every caller that shows a lot or a grid
  // passes one argument, so the sale's date can never linger under a lot's name.
  function setTitle(text, sub) {
    // Stripped for display: the badge in the corner already says which lot
    // this is. A no-op on anything that is not a lot title (the session
    // slideshow puts the sale name through here too).
    //
    // Safe because this element is WRITE-ONLY. On the host page the same
    // strip would not be: there the displayed title is read back as data
    // (getSellingProductTitle feeds the sold-list entry on commit and on
    // pass), so stripping the display would strip the record. TODO.md #5.
    var shownTitle = (window.MHV && MHV.stripLotPrefix) ? MHV.stripLotPrefix(text) : text;
    if (titleEl) titleEl.textContent = shownTitle || '\u00A0';
    if (subEl) {
      subEl.textContent = sub || '';
      subEl.style.display = sub ? '' : 'none';
    }
  }

  // ---- The session slideshow shows the SALE, not a lot ----------------------
  //
  // Shopify's photo-gallery product for an auction is titled AND handled with the date
  // code, so putting it on the block projected "20260823" to the room in 42px type.
  // What the room wants is the name of the sale and when it starts. The code is still
  // the right lookup key — it is the session code — so we trade it for the event.
  //
  // Cached per code and never re-fetched: this screen stays up all day and the answer
  // does not change. A failed lookup caches null and we keep showing the product title,
  // because a wrong-but-present title bar beats an empty one.
  var eventInfoCache = {};
  var currentSessionCode = null;

  function sessionCodeOf(product) {
    if (!product) return null;
    var h = String(product.handle || '').trim();
    if (/^\d{8}$/.test(h)) return h;
    var t = String(product.title || '').trim();
    if (/^\d{8}$/.test(t)) return t;
    return null;
  }

  // "2026/08/23-14:00" → "Sunday, August 23 · 2:00 PM ET". Eastern wall clock in, and
  // the string is built by hand rather than through a Date so no timezone can shift it.
  var PROJ_DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  var PROJ_MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];
  function formatLivetime(lt) {
    if (!lt) return '';
    var m = String(lt).match(/^(\d{4})\/(\d{2})\/(\d{2})(?:-(\d{2}):(\d{2}))?/);
    if (!m) return '';
    var y = +m[1], mo = +m[2], d = +m[3];
    // Local-midnight Date purely to name the weekday — no clock reading is taken from it.
    var dow = PROJ_DAYS[new Date(y, mo - 1, d).getDay()] || '';
    var out = (dow ? dow + ', ' : '') + PROJ_MONTHS[mo - 1] + ' ' + d;
    if (m[4] != null) {
      var h = +m[4], mi = +m[5];
      var ampm = h >= 12 ? 'PM' : 'AM';
      var h12 = h % 12; if (h12 === 0) h12 = 12;
      out += ' · ' + h12 + (mi ? ':' + (mi < 10 ? '0' + mi : mi) : ':00') + ' ' + ampm + ' ET';
    }
    return out;
  }

  function applySessionTitle(code, fallbackTitle) {
    var cached = eventInfoCache[code];
    if (cached !== undefined) {
      if (cached) setTitle(cached.title, cached.when);
      else setTitle(fallbackTitle);
      return;
    }
    setTitle(fallbackTitle);   // never blank while we ask
    fetch(MHV.SERVER_URL + '/api/schedule/live-status?session=' + encodeURIComponent(code))
      .then(function(r) { return r.json(); })
      .then(function(d) {
        var info = (d && d.title) ? { title: d.title, when: formatLivetime(d.livetime) } : null;
        eventInfoCache[code] = info;
        // Only paint if that slideshow is still the thing on screen.
        if (info && currentSessionCode === code) setTitle(info.title, info.when);
      })
      .catch(function() { eventInfoCache[code] = null; });
  }

  // Server sends metafields as an array: [{key, value, namespace}, ...]
  function getMetafield(product, key) {
    if (!product || !product.metafields) return '';
    // Handle array format (from getProductBySku)
    if (Array.isArray(product.metafields)) {
      for (var i = 0; i < product.metafields.length; i++) {
        if (product.metafields[i].key === key) return product.metafields[i].value || '';
      }
      return '';
    }
    // Handle flat object format (from collection cache)
    return product.metafields[key] || '';
  }

  function setLotBadge(product) {
    if (!lotBadgeEl) return;
    var lot = getMetafield(product, 'lot');
    if (!lot && product && product.title) {
      var m = product.title.match(/^Lot\s+(\S+)/i);
      if (m) lot = m[1];
    }
    if (lot) {
      // ⚠️ THE NUMBER, NOT "Lot 047". The title beneath already began
      // "Lot 047 - ", so the room was reading the word twice and the number
      // twice. The badge is the label; the digits are the only part that
      // varies, and from the back of a room they are the only part read.
      lotBadgeEl.textContent = lot;
      lotBadgeEl.style.display = 'block';
    } else {
      lotBadgeEl.style.display = 'none';
    }
  }

  function setAbsenteeBadge(product) {
    if (!absenteeBadgeEl) return;
    // DB first, metafield only as the pre-connect fallback. The metafield lags a
    // sync, so a lot bid on after it was written showed the room no absentee badge.
    var pid = String((product && (product.productId || product.id)) || '').replace(/[^0-9]/g, '');
    currentProductForBadge = pid || null;
    var live = pid ? liveBidCounts[pid] : undefined;
    var bidCount = (live !== undefined)
      ? live
      : parseInt(getMetafield(product, 'absentee_number_of_bids') || 0, 10);
    absenteeBadgeEl.style.display = bidCount > 0 ? 'block' : 'none';
  }

  // A late live-bid-state for the lot on screen must be able to TURN THE BADGE ON —
  // the badge is painted once when the lot goes up, which is before the engine has
  // answered.
  function refreshAbsenteeBadgeFor(productId) {
    if (!absenteeBadgeEl || !productId) return;
    var pid = String(productId).replace(/[^0-9]/g, '');
    if (!currentProductForBadge || currentProductForBadge !== pid) return;
    var n = liveBidCounts[pid];
    if (n !== undefined) absenteeBadgeEl.style.display = n > 0 ? 'block' : 'none';
  }

  function clearBadges() {
    if (lotBadgeEl) lotBadgeEl.style.display = 'none';
    if (absenteeBadgeEl) absenteeBadgeEl.style.display = 'none';
    if (bidPanelEl) bidPanelEl.style.display = 'none';
    // Forget the lot too. Without this the panel's "stays up" memory would carry
    // across a clear, and the next push for a different lot would be treated as a
    // continuation of the one just taken down.
    pbpProductId = null; pbpHasContent = false;
  }

  // ---- Slide display (self-contained, no live-slideshow.js dependency) ----
  function showSlide(index) {
    var slideshow = document.getElementById('product-slideshow');
    if (!slideshow) return;
    var slides = slideshow.querySelectorAll('.slide');
    if (slides.length === 0) return;
    if (index < 0 || index >= slides.length) return;

    for (var i = 0; i < slides.length; i++) {
      slides[i].classList.toggle('active', i === index);
    }
    currentSlideIndex = index;
  }

  // ---- Slide timing: THE SAME MODEL AS THE LIVE PAGE ------------------------
  // The room and the audience must never be looking at different photos, so this
  // is deliberately a copy of the live page's behaviour rather than something
  // tuned for a projector:
  //
  //   • a new lot starts advancing every 3s, immediately — not after a delay;
  //   • the first slide the HOST drives kills the local timer for that lot and
  //     the host owns it from then on;
  //   • a PRODUCT gets one pass and rests on its first photo, which is the shot
  //     the room should be looking at while the thing sells;
  //   • the SESSION gallery loops for ever — it is the standing "what's coming
  //     up" screen, and a frozen holding slide reads as broken.
  //
  // This replaced a 12-second "the host has gone quiet, take over" fallback.
  // That fallback was well-intentioned — a host tab throttled by Chrome does go
  // silent — but it made the projector the ONE screen advancing on its own, so
  // the room and the live page drifted out of step. A throttled host starves the
  // live page identically; the honest place to fix that is the host, not a
  // divergence on one screen.
  var localTimer = null;

  function stopLocalAutoplay() {
    if (localTimer) { clearInterval(localTimer); localTimer = null; }
  }

  function startLocalAutoplay() {
    stopLocalAutoplay();
    if (currentGridLots) return;                        // a grid has no slides
    if (!currentImages || currentImages.length < 2) return;
    localTimer = setInterval(function () {
      if (!hostPlaying) return;                         // a deliberate host pause
      var next = currentSlideIndex + 1;
      if (next < currentImages.length) { showSlide(next); return; }
      showSlide(0);
      if (!currentSessionCode) stopLocalAutoplay();
    }, 3000);
  }

  // ---- Slideshow display ----
  // `isTerms`: the auction-terms deck (mhv-terms-slides.js). Its slides are
  // built by the shared renderer, and it NEVER runs on the local timer — the
  // host walks it, one talking point at a time, over auction-terms-slide-update.
  function showProjectorSlideshow(images, product, isTerms) {
    var container = document.getElementById('product-slideshow-container');
    var slideshow = document.getElementById('product-slideshow');
    if (!container || !slideshow) return;

    if (!images || images.length === 0) {
      container.style.display = 'none';
      return;
    }

    // Set title and badges. A session slideshow shows the sale's name + date/time.
    currentSessionCode = sessionCodeOf(product);
    if (currentSessionCode) {
      applySessionTitle(currentSessionCode, (product && product.title) || '');
    } else if (product && product.title) {
      setTitle(product.title);
    }
    currentProductHandle = (product && product.handle) || null;
    setLotBadge(product);
    setAbsenteeBadge(product);

    // Build slides — a terms slide through the shared renderer, a photo as <img>.
    slideshow.innerHTML = '';
    for (var i = 0; i < images.length; i++) {
      if (window.MHVTermsSlides && window.MHVTermsSlides.isSlideEntry(images[i])) {
        slideshow.appendChild(window.MHVTermsSlides.slideElement(images[i], i));
        continue;
      }
      var img = document.createElement('img');
      img.src = images[i].url;
      img.alt = images[i].alt || ('Image ' + (i + 1));
      img.className = 'slide';
      img.setAttribute('data-index', String(i));
      slideshow.appendChild(img);
    }

    currentImages = images;
    currentSlideIndex = 0;
    lastHostSlideAt = Date.now();
    termsOnScreen = !!isTerms;

    // Show container and first slide
    container.style.display = 'block';
    showSlide(0);
    if (isTerms) { stopLocalAutoplay(); hostPlaying = false; }
    else startLocalAutoplay();
  }
  var termsOnScreen = false;

  function clearProjectorSlideshow() {
    stopLocalAutoplay();
    var container = document.getElementById('product-slideshow-container');
    var slideshow = document.getElementById('product-slideshow');
    if (container) container.style.display = 'none';
    if (slideshow) slideshow.innerHTML = '&nbsp;';
    currentImages = [];
    currentSlideIndex = 0;
    currentProductHandle = null;
    currentSessionCode = null;
    setTitle('\u00A0');
    clearBadges();
  }

  // ---- Multi-lot grid (One Money / Multi Money / On Choice) ----
  var currentGridLots = null;

  // Informative group banner: reflect the sale mode, e.g. "3 Lots × The Money",
  // "2 Lots — One Money", "4 Lots — Choice". Falls back to a plain count.
  function formatGroupLabel(count, saleMode) {
    var lots = count + (count === 1 ? ' Lot' : ' Lots');
    switch (saleMode) {
      case 'multi-money': return lots + ' × The Money';
      case 'one-money':   return lots + ' — One Money';
      case 'on-choice':   return lots + ' — Choice';
      default:            return 'Group of ' + lots;
    }
  }

  function renderProjectorGrid(lots, layout, hero, saleMode, chosenKeys, committedKeys) {
    var gridContainer = document.getElementById('product-grid-container');
    var slideshowContainer = document.getElementById('product-slideshow-container');
    if (!gridContainer) return;

    var cappedLots = Array.isArray(lots) ? lots.slice(0, 6) : [];
    var count = Math.max(1, Math.min(6, cappedLots.length));
    var mode = String(layout || count);
    var chosenSet = {}; (chosenKeys || []).forEach(function (k) { chosenSet[String(k)] = 1; });
    var committedSet = {}; (committedKeys || []).forEach(function (k) { committedSet[String(k)] = 1; });

    // Grid supersedes the single-product slideshow overlay
    if (slideshowContainer) slideshowContainer.style.display = 'none';

    gridContainer.className = 'product-grid-container projector-grid-container mode-grid-' + mode + (hero ? ' has-hero' : '');
    gridContainer.style.display = 'grid';

    var slots = gridContainer.querySelectorAll('.grid-slot');
    for (var s = 0; s < slots.length; s++) {
      var slot = slots[s];
      var slotIdx = parseInt(slot.getAttribute('data-slot'), 10) - 1;
      var img = slot.querySelector('.grid-slot-img');
      var title = slot.querySelector('.grid-slot-title');
      var lot = cappedLots[slotIdx];
      slot.classList.remove('empty');
      slot.classList.remove('blank');
      slot.classList.remove('chosen');
      slot.classList.remove('committed');
      if (!lot) {
        if ((mode === '3' && slot.dataset.slot === '4') || (mode === '5' && slot.dataset.slot === '6')) {
          slot.classList.add('blank');
        } else {
          slot.classList.add('empty');
        }
        if (img) { img.removeAttribute('src'); img.alt = ''; }
        if (title) title.textContent = '';
        continue;
      }
      // On Choice: green = picked by the winning bidder, gray = already sold.
      if (lot.choiceKey && committedSet[String(lot.choiceKey)]) slot.classList.add('committed');
      else if (lot.choiceKey && chosenSet[String(lot.choiceKey)]) slot.classList.add('chosen');
      var firstImage = (lot.product && Array.isArray(lot.product.images) && lot.product.images[0]) || null;
      var imgUrl = firstImage ? (firstImage.url || firstImage.src || '') : '';
      if (img) {
        if (imgUrl) {
          img.src = imgUrl.indexOf('?') >= 0 ? imgUrl + '&width=1400' : imgUrl + '?width=1400';
          img.alt = (lot.product && lot.product.title) || lot.sku || '';
        } else {
          img.removeAttribute('src');
          img.alt = '';
        }
      }
      if (title) {
        title.textContent = (lot.product && lot.product.title) || lot.sku || '';
      }
    }

    currentGridLots = cappedLots;
    currentSessionCode = null;
    // Projector title bar shows the sale-mode-aware group banner while a grid is active
    setTitle(formatGroupLabel(count, saleMode));
    clearBadges();
  }

  function clearProjectorGrid() {
    var gridContainer = document.getElementById('product-grid-container');
    var slideshowContainer = document.getElementById('product-slideshow-container');
    if (gridContainer) {
      gridContainer.style.display = 'none';
      gridContainer.className = 'product-grid-container projector-grid-container';
      var slots = gridContainer.querySelectorAll('.grid-slot');
      for (var i = 0; i < slots.length; i++) {
        var img = slots[i].querySelector('.grid-slot-img');
        var title = slots[i].querySelector('.grid-slot-title');
        if (img) { img.removeAttribute('src'); img.alt = ''; }
        if (title) title.textContent = '';
        slots[i].classList.remove('empty');
        slots[i].classList.remove('blank');
      }
    }
    if (slideshowContainer) slideshowContainer.style.display = '';
    currentGridLots = null;
  }

  // ---- Socket event handlers ----

  // Receive current lot (on connect + when host changes lot)
  socket.on('current-lot-updated', function(data) {
    // A new lot in SHOWING ends the last result. This is the ONLY thing that clears
    // it: not a timer, not the next bid state, not the commit finishing — the room
    // keeps looking at the number while the scribe is still typing, and a result
    // that disappears on its own is a result somebody missed.
    //
    // ⚠️ This event is not only lot changes. The same broadcast carries the
    // slideshow toggle and the VISIBILITY-TOGGLE-* controls, so clearing on all of
    // it meant hiding the lot list — or any stray toggle — wiped SOLD off the wall
    // mid-sale. Only an actual lot change counts.
    var _sku = data && data.sku ? String(data.sku) : '';
    var _isLotChange = !!_sku
      && data.action !== 'toggle-slideshow'
      && _sku.indexOf('VISIBILITY-TOGGLE-') !== 0;
    if (_isLotChange) clearSold();
    if (!data) return;

    // Single-lot updates supersede any active multi-lot grid
    if (currentGridLots) clearProjectorGrid();

    // Handle clear
    if (data.sku === 'CLEARED' && data.action === 'clear') {
      clearProjectorSlideshow();
      return;
    }

    // Handle visibility toggles (ignore)
    if (data.action === 'toggle-slideshow' || data.action === 'visibility-toggle') {
      return;
    }

    // Handle lot with product
    if (data.product) {
      // The server stores the owner's play state on the lot, so a projector that joins
      // (or rejoins) mid-lot knows whether the slideshow is meant to be running.
      hostPlaying = (data.slideshowPlaying !== false);
      var isTerms = !!(window.MHVTermsSlides && window.MHVTermsSlides.isTermsPayload(data));
      var images = [];
      if (data.product.images && data.product.images.length > 0) {
        for (var i = 0; i < data.product.images.length; i++) {
          var img = data.product.images[i];
          if (isTerms && img && typeof img === 'object' && img.kind) {
            // A terms slide: keep its content for the renderer.
            images.push({ kind: img.kind, url: img.url || null, alt: img.alt || '', content: img.content || {} });
          } else {
            images.push({ url: img.src || img.url || img, alt: img.alt || '' });
          }
        }
      }
      showProjectorSlideshow(images, data.product, isTerms);
      // Ask for this lot's bid state rather than waiting for the next bid to land.
      // Same reason the connect handler asks: a lot can sit on the block for a long
      // time between bids, and the room should not be looking at an empty panel.
      socket.emit('request-live-bid-state');

      // Late joiner: jump to current slide
      if (typeof data.slideIndex === 'number' && data.slideIndex > 0) {
        showSlide(data.slideIndex);
      }
    }
  });

  // Multi-lot grid broadcast (One Money / Multi Money / On Choice)
  socket.on('current-lot-grid-updated', function(data) {
    // A multi-lot grid has no single current bid, so the panel comes down and
    // forgets its lot along with it.
    if (bidPanelEl) bidPanelEl.style.display = 'none';
    pbpProductId = null; pbpHasContent = false;
    if (!data || data.mode === 'clear' || !Array.isArray(data.lots) || data.lots.length === 0) {
      clearProjectorGrid();
      return;
    }
    try { renderProjectorGrid(data.lots, data.layout, data.hero, data.saleMode, data.chosenKeys, data.committedKeys); }
    catch (e) { console.error('projector grid render error:', e); }
  });

  // Sync slides from host owner. Every one of these hands control BACK to the host —
  // the local fallback below only runs while these have stopped arriving.
  socket.on('slideshow-slide-changed', function(data) {
    if (!data) return;
    lastHostSlideAt = Date.now();
    hostPlaying = (data.isPlaying !== false);
    // The host is driving: hand it the slideshow and stop advancing ourselves.
    // Exactly what the live page does — its handler clears window.slideshowInterval
    // on the first slide the owner sends, and never restarts it for that lot.
    stopLocalAutoplay();
    if (currentImages.length === 0) return;
    showSlide(data.slideIndex);
  });

  // The host walking the auction-terms deck. The terms never run on the local
  // timer, so this is the ONLY thing that turns a page on the wall.
  socket.on('auction-terms-slide-update', function(data) {
    if (!data || typeof data.slideIndex !== 'number') return;
    if (!termsOnScreen || currentImages.length === 0) return;
    lastHostSlideAt = Date.now();
    stopLocalAutoplay();
    showSlide(data.slideIndex);
  });

  // Show absentee badge when a bid comes in for the current lot
  socket.on('absentee-bid-activity', function(data) {
    if (!data || data.type !== 'bid' || !absenteeBadgeEl) return;
    // Match by product handle if available
    if (currentProductHandle && data.productHandle === currentProductHandle) {
      absenteeBadgeEl.style.display = 'block';
    }
  });

  // Clear absentee badge when host clicks OUT
  socket.on('absentee-badge-clear', function() {
    if (absenteeBadgeEl) absenteeBadgeEl.style.display = 'none';
  });

  // Current bid + holder (ABSENTEE/FLOOR) + ask — driven by the host live-bid state.
  // ---- SOLD -----------------------------------------------------------------
  // The hammer, as the room sees it. Driven by the SUBMIT the scribe makes, not by
  // the commit that follows: submit is the moment the auctioneer said sold, and the
  // room should not wait on paperwork to see it.
  var soldEl = document.getElementById('pbp-sold');
  var soldPriceEl = document.getElementById('pbp-sold-price');
  var gridEl = document.getElementById('pbp-grid');
  var soldShowing = false;

  function showSold(price) {
    if (!soldEl || !bidPanelEl) return;
    soldShowing = true;
    if (soldPriceEl) soldPriceEl.textContent = pbpMoney(price);
    soldEl.style.display = '';
    if (gridEl) gridEl.style.display = 'none';
    bidPanelEl.classList.add('is-sold');
    bidPanelEl.style.display = wallHidden ? 'none' : '';
    pbpHasContent = true;
  }

  function clearSold() {
    if (!soldShowing) return;
    soldShowing = false;
    if (soldEl) soldEl.style.display = 'none';
    if (gridEl) gridEl.style.display = '';
    if (bidPanelEl) bidPanelEl.classList.remove('is-sold');
  }

  socket.on('selling-commit-updated', function (d) {
    if (!d) return;
    if (d.action === 'submit') {
      var p = Number(d.price || 0);
      if (p > 0) showSold(p);
    } else if (d.action === 'cancel') {
      // A submit taken back is not a sale. The wall must not keep saying it was.
      clearSold();
    }
    // 'complete' deliberately does nothing: the lot IS sold, and the result stays
    // up until the next lot replaces it.
  });

  // Bid state cannot repaint over a result — the numbers behind it are the ones
  // that produced it, and the room has stopped reading them.
  socket.on('live-bid-state', function (d) {
    if (d && typeof d.projectorPanel !== 'undefined') applyWall(d.projectorPanel === false);
    if (soldShowing) return;
    renderBidPanel(d);
  });

  // The host hid or showed the panel. Hiding is immediate; showing re-requests the
  // state so the panel comes back with the CURRENT numbers rather than whatever it
  // last painted (a SOLD result stays a SOLD result — that box is repainted here).
  function applyWall(hidden) {
    hidden = !!hidden;
    if (hidden === wallHidden) return;
    wallHidden = hidden;
    if (!bidPanelEl) return;
    if (wallHidden) { bidPanelEl.style.display = 'none'; return; }
    if (soldShowing) { bidPanelEl.style.display = ''; return; }
    bidPanelEl.style.display = pbpHasContent ? '' : 'none';
    if (socket && socket.connected) socket.emit('request-live-bid-state');
  }
  socket.on('projector-bid-panel-changed', function (d) { applyWall(!(d && d.visible)); });

  // ---- Move play/mute buttons into floating controls ----
  var controlsPanel = document.getElementById('projector-controls');
  var playBtn = document.getElementById('play-pause-btn');
  var muteBtn = document.getElementById('mute-toggle-btn');
  var fsBtn = document.getElementById('projector-fullscreen-btn');

  if (controlsPanel && fsBtn) {
    // Find the button row (second row)
    var btnRow = controlsPanel.querySelectorAll('.projector-controls-row')[1];
    if (btnRow) {
      if (playBtn) btnRow.insertBefore(playBtn, fsBtn);
      if (muteBtn) btnRow.insertBefore(muteBtn, fsBtn);
    }
  }

  // ---- Watchdog 1: the connection ----
  // 45s of silence is more than two missed heartbeats — force a reconnect rather than
  // trusting a socket that says it is fine. Still dark five minutes later, reload the
  // page outright: a projector showing a frozen frame is worse than a two-second reload.
  var STALE_MS = 45000;
  var DARK_RELOAD_MS = 5 * 60 * 1000;
  setInterval(function() {
    var now = Date.now();
    if (socket.connected) lastConnectedAt = now;
    if (!socket.connected && now - lastConnectedAt > DARK_RELOAD_MS) {
      console.warn('Projector: dark for 5 minutes — reloading');
      window.location.reload();
      return;
    }
    if (now - lastServerMsg > STALE_MS) {
      console.warn('Projector: no word from the server in ' + Math.round((now - lastServerMsg) / 1000) + 's — reconnecting');
      lastServerMsg = now;   // one attempt per window, not a hammer
      try { socket.disconnect(); } catch (e) {}
      try { socket.connect(); } catch (e) {}
    }
  }, 10000);

  // A projector is usually the foreground tab, but it can be un-minimised or woken with
  // the machine — take those as a cue to check in rather than waiting out the window.
  document.addEventListener('visibilitychange', function() {
    if (document.hidden) return;
    lastServerMsg = Date.now();
    if (!socket.connected) { try { socket.connect(); } catch (e) {} }
  });
  window.addEventListener('online', function() {
    if (!socket.connected) { try { socket.connect(); } catch (e) {} }
  });

  // ---- Watchdog 2 (removed) ----
  // Slide timing now lives in startLocalAutoplay/stopLocalAutoplay above, which
  // mirror the live page exactly. See the comment there for why a projector-only
  // "the host went quiet, take over" fallback was the wrong shape.

  // ---- Text size slider (affects title + lot badge) ----
  var sizeSlider = document.getElementById('projector-size-slider');

  function applyTextSize(size) {
    if (titleEl) titleEl.style.fontSize = size + 'px';
    // Half the title, so the sale's date reads as a subline at every slider position.
    if (subEl) subEl.style.fontSize = Math.max(11, Math.round(size * 0.5)) + 'px';
    if (lotBadgeEl) lotBadgeEl.style.fontSize = Math.round(size * 0.9) + 'px';
    // The bid panel scales too (tweak 8). Every size inside it is in `em`, so setting
    // ONE font-size here moves the label, the amounts and the FLOOR/ABSENTEE badge
    // together. The slider used to move only the title and lot badge, so the bid
    // figures — the numbers the room is actually reading — stayed fixed while
    // everything around them grew. 0.48 keeps the default 42 -> the original 20px.
    if (bidPanelEl) bidPanelEl.style.fontSize = Math.max(10, Math.round(size * 0.78)) + 'px';
  }

  // Apply on load ALWAYS, not only when a size was saved. Without this a screen
  // that has never touched the slider runs on the stylesheet's base sizes, so the
  // panel and the title are sized by two different rules and the slider's first
  // nudge makes everything jump.
  var savedSize = parseInt(localStorage.getItem('projectorTitleSize'), 10);
  if (!Number.isFinite(savedSize)) savedSize = sizeSlider ? parseInt(sizeSlider.value, 10) : 42;
  // A size saved before the ceiling was raised is still valid; one saved above it
  // (or corrupted) is clamped rather than trusted.
  if (sizeSlider) {
    var lo = parseInt(sizeSlider.min, 10) || 24;
    var hi = parseInt(sizeSlider.max, 10) || 200;
    savedSize = Math.min(hi, Math.max(lo, savedSize));
    sizeSlider.value = savedSize;
  }
  applyTextSize(savedSize);
  if (sizeSlider) {
    sizeSlider.addEventListener('input', function() {
      var size = parseInt(sizeSlider.value, 10);
      applyTextSize(size);
      localStorage.setItem('projectorTitleSize', size);
    });
  }

  // ---- Auto-hide controls after inactivity ----
  var hideTimeout = null;
  var HIDE_DELAY = 4000; // 4 seconds of inactivity

  function showControls() {
    if (controlsPanel) {
      controlsPanel.classList.remove('auto-hidden');
    }
    clearTimeout(hideTimeout);
    hideTimeout = setTimeout(hideControls, HIDE_DELAY);
  }

  function hideControls() {
    if (controlsPanel) {
      controlsPanel.classList.add('auto-hidden');
    }
  }

  // Show on mouse move anywhere on page
  document.addEventListener('mousemove', showControls);
  // Show on touch
  document.addEventListener('touchstart', showControls);
  // Keep visible while hovering over the controls
  if (controlsPanel) {
    controlsPanel.addEventListener('mouseenter', function() {
      clearTimeout(hideTimeout);
      controlsPanel.classList.remove('auto-hidden');
    });
    controlsPanel.addEventListener('mouseleave', function() {
      hideTimeout = setTimeout(hideControls, HIDE_DELAY);
    });
  }

  // Start the auto-hide timer
  hideTimeout = setTimeout(hideControls, HIDE_DELAY);

  // ---- Disable auto-unmute on click (projector should stay muted until manually toggled) ----
  window._projectorPage = true;

  // ---- Fullscreen toggle ----
  var projectorContainer = document.getElementById('projector-container');

  function toggleFullscreen() {
    if (!projectorContainer) return;
    if (!document.fullscreenElement && !document.webkitFullscreenElement) {
      if (projectorContainer.requestFullscreen) {
        projectorContainer.requestFullscreen();
      } else if (projectorContainer.webkitRequestFullscreen) {
        projectorContainer.webkitRequestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      }
    }
  }

  // Keyboard shortcut: F (or Ctrl/Cmd+F) toggles fullscreen; Esc exits is native.
  // Ignored while typing in a field (the size slider is the only input here).
  document.addEventListener('keydown', function(e) {
    if (e.key !== 'f' && e.key !== 'F') return;
    var t = e.target;
    if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
    // Ctrl/Cmd+F would open the browser's find bar — suppress it on the kiosk.
    if (e.ctrlKey || e.metaKey) e.preventDefault();
    if (e.altKey || e.shiftKey) return;
    toggleFullscreen();
    if (typeof showControls === 'function') showControls();
  });

  if (fsBtn && projectorContainer) {
    fsBtn.addEventListener('click', toggleFullscreen);

    // Update icon on fullscreen change
    function onFsChange() {
      var isFs = !!document.fullscreenElement || !!document.webkitFullscreenElement;
      var svg = fsBtn.querySelector('svg path');
      if (svg) {
        if (isFs) {
          svg.setAttribute('d', 'M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z');
        } else {
          svg.setAttribute('d', 'M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z');
        }
      }
    }
    document.addEventListener('fullscreenchange', onFsChange);
    document.addEventListener('webkitfullscreenchange', onFsChange);
  }

})();
